import moment from 'moment';
import { isEmpty, omit } from 'lodash';
import API from '../api';
import { restrictedState } from '../../common/FormWithSteps/Utilities';
import { planHelper, collectSavedPlanData } from './Paradigm/planHelper';
import { createSalesforceProposal } from './Salesforce/salesforceHelper';
import { saveData } from '../../common/FormWithSteps';
const api = API.create();

const CvFailSubject = 'Advisor Failed CV - Simply Retirement';

export const canPurchase = (employerStateCode, advisorCvStatus) => {
  if (
    !restrictedState(employerStateCode) ||
    employerStateCode === 'FL' ||
    employerStateCode === 'TX' ||
    employerStateCode === 'GA'
  )
    return true;
  if (restrictedState(employerStateCode)) {
    return advisorCvStatus.every((status) => status === 'PASS');
  }
  return false;
};

export const paradigmPurchase = async ({
  formData,
  authenticityToken,
  setPurchasePlanStatus,
  advisorCvStatuses,
  putPurchasePlanURL,
  resetData,
  tpaParadigmPackager = [],
  isOneDigital = false,
}) => {
  const { hasFp } = formData.planDesign.questions;
  const paradigmPlan = await planHelper(
    formData.planDesign,
    advisorCvStatuses,
    tpaParadigmPackager,
    authenticityToken,
    formData.role,
  );
  const data = {
    state: formData,
    paradigm_plan: paradigmPlan,
    authenticity_token: authenticityToken,
    email: formData.planDesign.questions.sponsorEmail.value,
  };

  if (hasFp.value) {
    data.paradigm_advisors = collectSavedPlanData(formData).paradigm_advisors;
  }

  if (formData.hashId) {
    data.hashId = formData.hashId;
  }

  const response = await api.paradigmPurchase(data);

  const { purchaseLink } = response.data;

  setPurchasePlanStatus(response.status);
  putPurchasePlanURL(purchaseLink, response.data.planId);
  if (response.status === 200 || response.status === 201) {
    const goToParadigm = () => {
      if (typeof window !== 'undefined') {
        window.location.href = purchaseLink;
      }
    };
    createSalesforceProposal({
      formData,
      authenticityToken,
      stageFlag: 'Purchase',
      link: formData.planURL,
      advisorCvStatuses,
      tpaParadigmPackager,
      hashId: formData.hashId,
      paradigmId: response.data.planId,
      isOneDigital,
    });
    sessionStorage.clear();
    resetData();
    goToParadigm();
  }
};

const canCV = (advisor, checkCv) => {
  const principalError = {};
  if (!checkCv) return principalError;
  if (advisor.advisorCredential.eid.error) {
    principalError.message = advisor.advisorCredential.eid.error;
    principalError.subject = 'NPN Not Found - Simply Retirement ';
    principalError.code = advisor.advisorCredential.eid.code;
  }
  return principalError;
};

export const handleCV = async (
  formData,
  authenticityToken,
  setPurchasePlanStatus,
  putPurchasePlanURL,
  resetData,
  tpaParadigmPackager = [],
  setAdvisorCvDataAndResponse,
  uuid,
) => {
  const date = moment().format('YYYY-MM-DD');
  const {
    investmentLineup,
    employerStateCode,
    advisorPoints,
    advisorCompensation,
  } = formData.planDesign.questions;

  const advisorCvStatus = [];
  const advisors = [];

  await Promise.all(
    formData.planDesign.advisors.map(async (advisor, index) => {
      const { advisorNpn, firmName } = advisor.questions;
      const { advisorCredential, advisorCredentialVerification } = advisor;
      const { brokerType, brokerEid } = advisorCredentialVerification;
      const { eid } = advisorCredential;

      const checkCv =
        brokerType === 'insuranceAgent' ||
        brokerType === 'brokerDealer' ||
        brokerType === 'iarRia';
      const advisorCVdata = {
        businessEvent: 'COMMIT-CHANGED',
        writtenState: employerStateCode.value,
        ownersResidentState: employerStateCode.value,
        productCategory: 'GROUP-ANNUITY',
        applicationSignedDate: date,
        transactionEffectiveDate: date,
        mutualFund: investmentLineup.value !== 'fundLineup1',
        levelized:
          advisorCompensation.value === 'option_1'
            ? true
            : advisorPoints.valueFormatted !== '25',
      };

      if (formData.role === 'psivet') {
        advisorCVdata.distributionPartners = [
          {
            role: 'Career-Agent',
            enterpriseId: eid.value,
          },
        ];
      } else if (
        firmName.valueFormatted === 'PRINCIPAL SECURITIES INC' ||
        brokerType === 'insuranceAgent'
      ) {
        advisorCVdata.distributionPartners = [
          {
            role: 'Advisor',
            enterpriseId: eid.value,
          },
        ];
      } else if (brokerType === 'brokerDealer') {
        advisorCVdata.distributionPartners = [
          {
            role: 'Advisor',
            enterpriseId: eid.value,
          },
          {
            role: 'Broker-Dealer',
            enterpriseId: brokerEid,
          },
        ];
      } else {
        advisorCVdata.distributionPartners = [
          {
            role: 'IAR',
            enterpriseId: eid.value,
          },
        ];
      }

      let cvResponseArray = null;
      let response;

      const principalError = canCV(advisor, checkCv);
      if (isEmpty(principalError) && advisorNpn.value !== '') {
        if (advisorCredentialVerification.brokerEid !== '') {
          advisorCVdata.brokerDealer = advisorCredentialVerification.brokerEid;
        }
        response = await api.advisorCV({
          ...advisorCVdata,
          authenticity_token: authenticityToken,
        });
        advisorCvStatus[index] = response.data.credentialStatusText;
        cvResponseArray = response.data.credentialFailureMessages;

        if (response.data.qualificationError) {
          principalError.subject = CvFailSubject;
          principalError.message = response.data.qualificationError;
        }
        principalError.code = response.data.code;
      }

      if (
        advisorCvStatus[index] !== 'PASS' &&
        isEmpty(omit(principalError, 'code'))
      ) {
        principalError.subject = CvFailSubject;
        principalError.message = {
          credentialStatusText: advisorCvStatus[index],
        };
      }

      if (!isEmpty(omit(principalError, 'code'))) {
        if (restrictedState(employerStateCode.value)) {
          api.savePlan({
            ...collectSavedPlanData(formData),
            authenticity_token: authenticityToken,
          });
        }
        principalCvFail(
          formData.planDesign,
          advisor,
          principalError,
          date,
          authenticityToken,
          cvResponseArray,
          advisorCVdata.distributionPartners,
        );

        advisorCvStatus[index] = 'FAIL';
      }
      const cvReferenceData = {
        ...advisorCVdata,
        principalError,
        cvResponse: {
          status: advisorCvStatus[index],
          message: response?.data.credentialVerificationMessages,
        },
      };
      setAdvisorCvDataAndResponse({ ...cvReferenceData }, index);
      advisors[index] = setAdvisorCvData(formData, cvReferenceData, advisor);
    }),
  ).then(() => {
    const sessionData = { ...formData };
    sessionData.planDesign.advisors = advisors;
    const savePlanData = collectSavedPlanData(sessionData);

    saveData(uuid, sessionData);
    api.savePlan({
      ...savePlanData,
      authenticity_token: authenticityToken,
    });
  });

  if (canPurchase(employerStateCode.value, advisorCvStatus)) {
    paradigmPurchase({
      formData,
      authenticityToken,
      setPurchasePlanStatus,
      advisorCvStatuses: advisorCvStatus,
      putPurchasePlanURL,
      resetData,
      tpaParadigmPackager,
    });
  } else {
    setPurchasePlanStatus('advisor-error');
  }
};

const setAdvisorCvData = (state, obj, advisor) => {
  const update = {
    ...advisor,
    advisorCvData: {
      ...advisor.advisorCvData,
      state: obj.writtenState,
      date: obj.applicationSignedDate,
      mutualFund: obj.mutualFund,
      levelized: obj.levelized,
      brokerType: advisor.advisorCredentialVerification.brokerType,
      distributionPartners: obj.distributionPartners,
      principalError: obj.principalError,
      cvResponse: obj.cvResponse,
    },
  };
  return update;
};

const getCompensation = (planDesign) => {
  const { advisorFee, advisorPoints } = planDesign.questions;
  const { brokerType } = planDesign.advisors[0].advisorCredentialVerification;
  if (advisorFee.value) {
    return 'Flat Fee';
  }
  if (advisorPoints.value) {
    return brokerType === 'iarRia' ? 'RIA Basis Points' : 'Basis Points';
  }
  return `Billed Separately`;
};

export const principalCvFail = async (
  planDesign,
  advisor,
  failureMessage,
  date,
  authenticityToken,
  cvResponseArray,
  distributionPartners,
) => {
  const {
    sponsorFirstName,
    sponsorLastName,
    employerStateCode,
    advisorFee,
    advisorPoints,
    companyName,
    investmentLineup,
    planEffectiveDate,
  } = planDesign.questions;
  const {
    advisorPhone,
    advisorNpn,
    advisorFirstName,
    advisorLastName,
    advisorEmail,
    firmName,
  } = advisor.questions;

  try {
    await api.cvFail({
      fail_data: {
        accountState: employerStateCode.value,
        advisorEid: advisor.advisorCredential.eid.value,
        advisorEmail: advisorEmail.value,
        advisorName: `${advisorFirstName.value} ${advisorLastName.value}`,
        advisorNpn: advisorNpn.value,
        advisorPhone: advisorPhone.valueFormatted,
        brokerEid: advisor.advisorCredentialVerification.brokerEid,
        brokerType: advisor.advisorCredentialVerification.brokerType,
        clientName: `${sponsorFirstName.value} ${sponsorLastName.value}`,
        companyName: companyName.value,
        compensation: getCompensation(planDesign),
        crd: advisor.advisorCredentialVerification.crd.value,
        distributionPartners,
        cvResponseArray,
        date,
        failureMessage,
        planEffectiveDate: planEffectiveDate.valueFormatted,
        firmName: firmName.value.label,
        httpCode: failureMessage.code,
        insuranceAgent: advisor.advisorCredentialVerification.insuranceAgent,
        insuranceAgentNpn:
          advisor.advisorCredentialVerification.insuranceAgentNpn,
        mutualFund: investmentLineup.value !== 'fundLineup1',
        scale: advisorFee.valueFormatted || advisorPoints.valueFormatted,
      },
      authenticity_token: authenticityToken,
    });
  } catch (error) {
    // fail silently
  }
};

export const restrictedStateCvFail = async (
  planDesign,
  advisor,
  hashId,
  authenticityToken,
) => {
  const {
    sponsorFirstName,
    sponsorLastName,
    employerStateCode,
    companyName,
    sponsorEmail,
    companyPhone,
  } = planDesign.questions;
  try {
    await api.restrictedStateCvFail({
      fail_data: {
        clientName: `${sponsorFirstName.value} ${sponsorLastName.value}`,
        company: companyName.value,
        accountState: employerStateCode.value,
        clientEmail: sponsorEmail.value,
        clientPhone: companyPhone.valueFormatted,
        recipient: advisor.questions.advisorEmail.value,
        hashId,
      },
      authenticity_token: authenticityToken,
    });
  } catch (error) {
    // fail silently
  }
};
